import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/utils';

/**
 * THE WORKSPACE'S TIME ZONE, from every zone the browser knows (owner, 17 Oct
 * 2026: Trendia's ad accounts and store run on America/Chicago, and the list
 * offered seven zones without it). Searchable by city, zone, long name
 * ("central") or offset ("-05"). The server checks the pick against the zones
 * Postgres knows before saving (api config/timeZones).
 */

// Browsers without Intl.supportedValuesOf (before Chrome 99 / Safari 15.4).
const FALLBACK = [
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'Europe/Helsinki', 'Europe/Berlin',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Australia/Sydney', 'UTC',
];

export function allZones(current) {
  let list = [];
  try { list = Intl.supportedValuesOf('timeZone'); } catch { list = []; }
  const zones = new Set(list.length ? list : FALLBACK);
  zones.add('UTC');
  if (current) zones.add(current);
  return [...zones];
}

function zonePart(zone, style, at) {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: style })
      .formatToParts(at).find((p) => p.type === 'timeZoneName')?.value || '';
  } catch { return ''; }
}

/** America/Chicago -> { offset: 'UTC−05:00', minutes: -300, long: 'Central Daylight Time' }, today. */
export function describeZone(zone, at = new Date()) {
  const raw = zonePart(zone, 'longOffset', at);            // "GMT-05:00", or "GMT" at zero
  const m = /GMT([+-])(\d{2}):(\d{2})/.exec(raw);
  const minutes = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
  const offset = m ? `UTC${m[1] === '-' ? '−' : '+'}${m[2]}:${m[3]}` : 'UTC+00:00';
  return { zone, minutes, offset, long: zonePart(zone, 'long', at) };
}

export function TimeZonePicker({ value, onChange, className }) {
  const [open, setOpen] = useState(false);
  const current = useMemo(() => describeZone(value), [value]);
  // Every zone described once, when the list first opens: west to east.
  const zones = useMemo(() => (open
    ? allZones(value).map((z) => describeZone(z)).sort((a, b) => a.minutes - b.minutes || a.zone.localeCompare(b.zone))
    : []), [open, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          aria-label="Time zone"
          className={cn('flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm hover:border-primary/40 focus:outline-none focus:ring-1 focus:ring-ring', className)}
        >
          <span className="truncate">{value}</span>
          <span className="flex shrink-0 items-center gap-1.5 text-[12.5px] tabular-nums text-muted-foreground">
            {current.offset}
            <ChevronsUpDown className="size-3.5 opacity-50" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[max(var(--radix-popover-trigger-width),340px)] max-w-[calc(100vw-2rem)] p-0">
        <Command>
          <CommandInput placeholder="Search a city, zone or offset…" className="text-[13px]" />
          <CommandList className="max-h-[min(55vh,360px)] p-1">
            <CommandEmpty className="py-6 text-center text-[13px] text-muted-foreground">No time zone matches.</CommandEmpty>
            {zones.map((z) => {
              const picked = z.zone === value;
              return (
                <CommandItem
                  key={z.zone}
                  value={`${z.zone} ${z.zone.replace(/_/g, ' ')} ${z.long} ${z.offset} ${z.offset.replace('−', '-')}`}
                  onSelect={() => { onChange(z.zone); setOpen(false); }}
                  className={cn('items-center gap-3 rounded-md px-2.5 py-1.5', picked && 'bg-primary-soft')}
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className={cn('truncate text-[13.5px] font-medium', picked && 'text-primary')}>{z.zone.replace(/_/g, ' ')}</span>
                    {z.long && <span className="truncate text-[12px] text-muted-foreground">{z.long}</span>}
                  </span>
                  <span className="shrink-0 text-[12px] tabular-nums text-muted-foreground">{z.offset}</span>
                  {picked && <Check className="size-4 shrink-0 text-primary" />}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * One clock under two names (Asia/Calcutta and Asia/Kolkata) is one zone:
 * the same UTC offset in January, in July and today (api adAccountZones).
 */
export function sameClock(a, b, now = new Date()) {
  if (a === b) return true;
  const y = now.getUTCFullYear();
  return [new Date(Date.UTC(y, 0, 15, 12)), new Date(Date.UTC(y, 6, 15, 12)), now]
    .every((t) => zonePart(a, 'longOffset', t) !== '' && zonePart(a, 'longOffset', t) === zonePart(b, 'longOffset', t));
}

const hoursText = (min) => {
  const h = Math.abs(min) / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} hour${h === 1 ? '' : 's'}`;
};

/**
 * The zone the workspace's Meta ad accounts count their days in, when it is
 * not the workspace's: a day cut at two different midnights puts visits and
 * link clicks on different days, and short windows read Arrived above link
 * clicks. `ads` is GET /api/destinations/meta-ads/accounts. Null when there
 * is nothing to suggest.
 */
export function adZoneHint(ads, current, at = new Date()) {
  if (!ads?.connected || !Array.isArray(ads.accounts)) return null;
  const chosen = new Set((ads.chosen || []).map(String));
  const used = ads.accounts.filter((a) => chosen.has(String(a.id)) && a.timezone);
  if (!used.length) return null;
  const zones = [...new Set(used.map((a) => a.timezone))];
  if (zones.some((z) => sameClock(z, current))) return null;
  const zone = (used.find((a) => a.main) || used[0]).timezone;
  const gap = describeZone(zone, at).minutes - describeZone(current, at).minutes;
  const plural = used.length > 1;
  const which = zones.length > 1 ? `${zones.join(' and ')} (the main one: ${zone})` : zone;
  return {
    zone,
    text: `Your Meta ad account${plural ? 's count their' : ' counts its'} days in ${which}`
      + (gap ? `, ${hoursText(gap)} ${gap < 0 ? 'behind' : 'ahead of'} ${current}` : '')
      + '. Use the same zone so a day here is the same day as in Ads Manager.',
  };
}
